import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';

const RECENT_LISTINGS_PAGE_SIZE = 3;
const LISTING_CARD_PUBLIC_DATA_FIELD_KEYS = [
  'quantity',
  'qty',
  'requestedQuantity',
  'requestQuantity',
  'requiredQuantity',
  'neededQuantity',
  'quantityNeeded',
  'quantityRequired',
  'amount',
  'quantityUnit',
  'requestedQuantityUnit',
  'requestQuantityUnit',
  'unit',
  'unitLabel',
  'uom',
  'package',
  'packaging',
  'container',
  'containerType',
];

const uniqueCompact = values =>
  Array.from(new Set((values || []).filter(value => value != null && value !== '')));

const configuredPublicListingFieldKeys = listingFields =>
  (listingFields || [])
    .filter(fieldConfig => (fieldConfig.scope || 'public') === 'public')
    .map(fieldConfig => fieldConfig.key);

const categoryPublicDataKeys = categoryConfiguration => {
  const { key, categoryLevelKeys } = categoryConfiguration || {};
  return categoryLevelKeys?.length
    ? categoryLevelKeys
    : key
    ? [`${key}1`, `${key}2`, `${key}3`]
    : [];
};

const cardPublicDataFieldKeys = config =>
  uniqueCompact([
    ...LISTING_CARD_PUBLIC_DATA_FIELD_KEYS,
    ...configuredPublicListingFieldKeys(config?.listing?.listingFields),
    ...categoryPublicDataKeys(config?.categoryConfiguration),
  ]);

const resultIds = data => {
  const listings = data.data;
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);
};

const queryRecentListingsPayloadCreator = ({ config }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      sort: 'createdAt',
      perPage: RECENT_LISTINGS_PAGE_SIZE,
      minStock: 1,
      stockMode: 'match-undefined',
      include: ['author', 'images', 'currentStock'],
      'fields.listing': uniqueCompact([
        'title',
        'description',
        'geolocation',
        'price',
        'deleted',
        'state',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.cardStyle',
        'publicData.expiresAt',
        'publicData.expiryTimezone',
        'publicData.expiryStatus',
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
        'publicData.priceVariationsEnabled',
        'publicData.priceVariants',
        ...cardPublicDataFieldKeys(config).map(key => `publicData.${key}`),
      ]),
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    })
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error);
    });
};

export const queryRecentListings = createAsyncThunk(
  'LandingPage/queryRecentListings',
  queryRecentListingsPayloadCreator
);

const landingPageSlice = createSlice({
  name: 'LandingPage',
  initialState: {
    recentListingIds: [],
    recentListingsInProgress: false,
    recentListingsError: null,
  },
  extraReducers: builder => {
    builder
      .addCase(queryRecentListings.pending, state => {
        state.recentListingsInProgress = true;
        state.recentListingsError = null;
      })
      .addCase(queryRecentListings.fulfilled, (state, action) => {
        state.recentListingIds = resultIds(action.payload.data);
        state.recentListingsInProgress = false;
      })
      .addCase(queryRecentListings.rejected, (state, action) => {
        state.recentListingsInProgress = false;
        state.recentListingsError = action.payload;
      });
  },
});

export default landingPageSlice.reducer;

export const loadData = (params, search, config) => (dispatch, getState) => {
  const state = getState();
  const currentUser = state.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData =
    !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);

  return canFetchData ? dispatch(queryRecentListings({ config })) : Promise.resolve();
};
